import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useSites } from '@/hooks/useSites';
import { mockSites, mockSiteClients, mockSiteAlerts, mockServiceLogs } from '@/data/mockData';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Activity, AlertTriangle, Calendar } from 'lucide-react';
import { startOfDay, startOfWeek, startOfMonth, subDays, subWeeks, subMonths, format, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval } from 'date-fns';

interface OccupancyData {
  date: string;
  occupancy: number;
  capacity: number;
  rate: number;
}

interface ServiceData {
  name: string;
  value: number;
  color: string;
}

interface AlertData {
  date: string;
  critical: number;
  warning: number;
  info: number;
}

const COLORS = {
  primary: 'hsl(var(--primary))',
  secondary: 'hsl(var(--secondary))',
  accent: 'hsl(var(--accent))',
  destructive: 'hsl(var(--destructive))',
  warning: '#f59e0b',
  info: '#3b82f6',
  metro: '#8b5cf6',
  laundry: '#ec4899',
  meal: '#f97316',
  juice: '#06b6d4',
};

export function AnalyticsSection({ isDemo = false }: { isDemo?: boolean }) {
  const [timeRange, setTimeRange] = useState<'7days' | '30days' | '90days'>('30days');
  const [occupancyData, setOccupancyData] = useState<OccupancyData[]>([]);
  const [serviceData, setServiceData] = useState<ServiceData[]>([]);
  const [alertData, setAlertData] = useState<AlertData[]>([]);
  const [newIntakes, setNewIntakes] = useState(0);
  const [loading, setLoading] = useState(false);
  const { sites: dbSites } = useSites();

  const sites = isDemo ? mockSites : dbSites;
  const totalCapacity = sites.reduce((sum, site) => sum + (site.capacity || 0), 0);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange, sites, isDemo]);

  const getDateRange = () => {
    const now = new Date();
    switch (timeRange) {
      case '7days':
        return { start: subDays(now, 7), end: now, interval: 'day' };
      case '30days':
        return { start: subDays(now, 30), end: now, interval: 'day' };
      case '90days':
        return { start: subDays(now, 90), end: now, interval: 'week' };
      default:
        return { start: subDays(now, 30), end: now, interval: 'day' };
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const { start, end, interval } = getDateRange();

      // Use mock data for demo
      let clients, services, alerts;
      
      if (isDemo) {
        const allClients = Object.values(mockSiteClients).flat();
        clients = allClients;
        services = mockServiceLogs;
        alerts = mockSiteAlerts;
      } else {
        // Load occupancy data
        const { data: clientData } = await supabase
          .from('clients')
          .select('status, check_in_time, check_out_time, created_at')
          .gte('created_at', start.toISOString());
        clients = clientData;
      }

      setNewIntakes(clients?.length || 0);


      // Generate date intervals
      let dateIntervals;
      if (interval === 'day') {
        dateIntervals = eachDayOfInterval({ start, end });
      } else if (interval === 'week') {
        dateIntervals = eachWeekOfInterval({ start, end });
      } else {
        dateIntervals = eachMonthOfInterval({ start, end });
      }

      const occupancyTrend: OccupancyData[] = dateIntervals.map(date => {
        const dateStr = format(date, 'MMM dd');
        const checkedIn = clients?.filter(c => {
          const checkIn = c.check_in_time ? new Date(c.check_in_time) : new Date(c.created_at);
          const checkOut = c.check_out_time ? new Date(c.check_out_time) : null;
          return checkIn <= date && (!checkOut || checkOut > date);
        }).length || 0;

        const rate = totalCapacity > 0 ? Math.round((checkedIn / totalCapacity) * 100) : 0;

        return {
          date: dateStr,
          occupancy: checkedIn,
          capacity: totalCapacity,
          rate
        };
      });

      setOccupancyData(occupancyTrend);

      // Load service data
      if (!isDemo) {
        const { data: serviceData } = await supabase
          .from('service_logs')
          .select('service_type, quantity')
          .is('deleted_at', null)
          .gte('created_at', start.toISOString());
        services = serviceData;
      }

      const serviceStats: ServiceData[] = [
        {
          name: 'Transit',
          value: services?.filter(s => s.service_type === 'metro').reduce((sum, s) => sum + s.quantity, 0) || 0,
          color: COLORS.metro
        },
        {
          name: 'Laundry',
          value: services?.filter(s => s.service_type === 'laundry').reduce((sum, s) => sum + s.quantity, 0) || 0,
          color: COLORS.laundry
        },
        {
          name: 'Meals',
          value: services?.filter(s => s.service_type === 'meal').reduce((sum, s) => sum + s.quantity, 0) || 0,
          color: COLORS.meal
        },
        {
          name: 'Juice',
          value: services?.filter(s => s.service_type === 'juice').reduce((sum, s) => sum + s.quantity, 0) || 0,
          color: COLORS.juice
        }
      ];

      setServiceData(serviceStats);

      // Load alert data
      if (!isDemo) {
        const { data: alertData } = await supabase
          .from('alerts')
          .select('type, created_at')
          .gte('created_at', start.toISOString());
        alerts = alertData;
      }

      const alertTrend: AlertData[] = dateIntervals.map(date => {
        const nextDate = interval === 'day' 
          ? new Date(date.getTime() + 24 * 60 * 60 * 1000)
          : interval === 'week'
          ? new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000)
          : new Date(date.getTime() + 30 * 24 * 60 * 60 * 1000);

        const dateAlerts = alerts?.filter(a => {
          const alertDate = new Date(a.created_at);
          return alertDate >= date && alertDate < nextDate;
        }) || [];

        return {
          date: format(date, 'MMM dd'),
          critical: dateAlerts.filter(a => a.type === 'critical').length,
          warning: dateAlerts.filter(a => a.type === 'warning').length,
          info: dateAlerts.filter(a => a.type === 'info').length
        };
      });

      setAlertData(alertTrend);

    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentOccupancy = occupancyData[occupancyData.length - 1]?.occupancy || 0;
  const currentRate = occupancyData[occupancyData.length - 1]?.rate || 0;
  const totalServices = serviceData.reduce((sum, s) => sum + s.value, 0);
  const totalAlerts = alertData.reduce((sum, a) => sum + a.critical + a.warning + a.info, 0);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Analytics Dashboard</h2>
          <p className="text-xs text-muted-foreground">Performance metrics across all facilities</p>
        </div>
        <Select value={timeRange} onValueChange={(v: any) => setTimeRange(v)}>
          <SelectTrigger className="w-32 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7days">Last 7 Days</SelectItem>
            <SelectItem value="30days">Last 30 Days</SelectItem>
            <SelectItem value="90days">Last 90 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">New Intakes</p>
                <p className="text-xl font-bold">{newIntakes}</p>
                <p className="text-xs text-muted-foreground">
                  in last {timeRange === '7days' ? '7' : timeRange === '30days' ? '30' : '90'} days
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Services Provided</p>
                <p className="text-xl font-bold">{totalServices}</p>
                <p className="text-xs text-muted-foreground">total services</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-secondary/10 flex items-center justify-center">
                <Activity className="h-5 w-5 text-secondary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Alerts</p>
                <p className="text-xl font-bold">{totalAlerts}</p>
                <p className="text-xs text-muted-foreground">in period</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Facilities</p>
                <p className="text-xl font-bold">{sites.length}</p>
                <p className="text-xs text-muted-foreground">active sites</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="occupancy" className="space-y-3">
        <TabsList className="h-8">
          <TabsTrigger value="occupancy" className="text-xs">Occupancy Trends</TabsTrigger>
          <TabsTrigger value="alerts" className="text-xs">Alert Frequency</TabsTrigger>
        </TabsList>

        <TabsContent value="occupancy">
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Occupancy Over Time</CardTitle>
              <CardDescription className="text-xs">Daily occupancy rates across all facilities</CardDescription>
            </CardHeader>
            <CardContent className="p-3">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={occupancyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="date" 
                    stroke="hsl(var(--muted-foreground))"
                    style={{ fontSize: '11px' }}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    style={{ fontSize: '11px' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Line 
                    type="monotone" 
                    dataKey="occupancy" 
                    stroke={COLORS.primary} 
                    strokeWidth={2}
                    name="Occupancy"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="capacity" 
                    stroke={COLORS.secondary} 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Capacity"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts">
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Alert Frequency</CardTitle>
              <CardDescription className="text-xs">Alert distribution by severity over time</CardDescription>
            </CardHeader>
            <CardContent className="p-3">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={alertData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="date" 
                    stroke="hsl(var(--muted-foreground))"
                    style={{ fontSize: '11px' }}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    style={{ fontSize: '11px' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="critical" stackId="a" fill={COLORS.destructive} name="Critical" />
                  <Bar dataKey="warning" stackId="a" fill={COLORS.warning} name="Warning" />
                  <Bar dataKey="info" stackId="a" fill={COLORS.info} name="Info" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
